import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { APIService, KPI } from '../api';
import { PerimetersService } from './perimeters.service';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { UtilsService } from './utils.service';
import * as R from 'ramda';

@Injectable({
  providedIn: 'root'
})
export class PerimeterPathwayService {

  constructor(
    private readonly apiService: APIService,
    private readonly perimetersService: PerimetersService,
  ) { }

  private _pathways: { [key: string]: Observable<KPI.ObjectivesPathway> } = {};
  public requestPathway$(sites: KPI.Perimeter[], years: number[]): Observable<KPI.ObjectivesPathway> {
    const key = UtilsService.getKey(arguments);
    if (!this._pathways[key]) {
      this._pathways[key] = this.apiService.get_energy_objectives({
        buildings: PerimetersService.getBuildingsIds(sites).join(','),
        start_year: Math.min(...years),
        end_year: Math.max(...years),
      }).pipe(
        shareReplay(1)
      );
    }
    return this._pathways[key];
  }

  public getGlobalPathway$(sites: KPI.Perimeter[], years: number[]): Observable<KPI.PathwayProviderObjectives> {
    return this.requestPathway$(sites, years).pipe(
      map(({ provider_objectives }) => provider_objectives),
    );
  }

  private selectedPathwaySubject: BehaviorSubject<KPI.SelectedToUPathway | null> = new BehaviorSubject<KPI.SelectedToUPathway | null>(null);
  selectedPathway$: Observable<KPI.SelectedToUPathway | null> = this.selectedPathwaySubject.asObservable();

  public setSelectedPathway(pathway: KPI.SelectedToUPathway): void {
    this.selectedPathwaySubject.next(pathway);
  }

  private pathwayStartYear = new Date().getFullYear() - 3;
  private yearsRange: number[] = R.range(this.pathwayStartYear, 2051);
  public getSelectedPathway(): Observable<KPI.SelectedToUPathway | null> {
    return this.selectedPathway$.pipe(
      switchMap((pathway) => {
        if (pathway) {
          return of(pathway);
        }
        return this.perimetersService.perimeters$.pipe(
          switchMap((perimeters) => this.requestPathway$(perimeters, this.yearsRange)),
          map((pathway) => this.getDefaultSelectedPathway(pathway.provider_objectives.country_objectives))
        )
      }),
    );
  }

  public getDefaultSelectedPathway(pathway: KPI.PathwayCountryObjectives[]): KPI.SelectedToUPathway {
    const targetYears = [2030, 2040, 2050];

    const typeOfUseObjectives = pathway[0].type_of_use_objectives;
    const selectedTypeOfUseObjectives = typeOfUseObjectives[0];
    if (!selectedTypeOfUseObjectives) {
      return null;
    }
    const emissions = targetYears.reduce((acc, year) => {
      const objective = selectedTypeOfUseObjectives.objectives?.find(obj => obj.year === year);
      acc[year] = objective ? objective.emission : null;
      return acc;
    }, {} as { [key: number]: number | null });

    const consumptions = targetYears.reduce((acc, year) => {
      const objective = selectedTypeOfUseObjectives.objectives?.find(obj => obj.year === year);
      acc[year] = objective ? objective.consumption : null;
      return acc;
    }, {} as { [key: number]: number | null });

    const defaultPathway: KPI.SelectedToUPathway = {
      name: `${pathway[0].country.name} - ${selectedTypeOfUseObjectives.type_of_use_label}`,
      emission: emissions,
      consumption: consumptions
    };

    return defaultPathway
  }

}
